import type { TypedSupabaseClient } from '../client';
import type { Json, TablesUpdate } from '../../../supabase-client/src/database.types';
import type {
  IArticleRepository,
  IArticleCategoryRepository,
  IArticleFamilyRepository,
  IArticleBrandRepository,
  Article,
  ArticleBrand,
  ArticleCategory,
  ArticleFamily,
  ArticleFilters,
  ArticleSearchField,
  ArticleValuationRow,
  CatalogStats,
  CreateArticleInput,
  CreateArticleBrandInput,
  CreateArticleCategoryInput,
  UpdateArticleInput,
} from '../../../interfaces/src';
import { ARTICLE_SEARCH_FIELDS } from '../../../interfaces/src';
import { consumeEntityCode, peekEntityCode } from './entity-codes';
import {
  fetchAllRows,
  POSTGREST_PAGE_SIZE,
  withStableOrder,
} from './fetch-all-rows';
import {
  orContainsAnyColumn,
  orEqualsCaseInsensitiveAnyColumn,
} from './postgrest-search';
import {
  runProPricePropagation,
  touchesProPricing,
} from './pro-price-propagation';
import { isBareStoragePath, resolveStorageImageUrl } from './storage-image';
import { normalizeInitialArticleStocks } from '@erp/domain/inventory';
import { sanitizePackagingUnits } from '@erp/domain/sales';

function newArticleEntityId(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return uuid;
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
    /[xy]/g,
    (character) => {
      const value = Math.floor(Math.random() * 16);
      return (character === 'x' ? value : (value & 0x3) | 0x8).toString(16);
    },
  );
}

export class SupabaseArticleCategoryRepository implements IArticleCategoryRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async list(): Promise<ArticleCategory[]> {
    const rows = await fetchAllRows<Record<string, unknown>>((from, to) =>
      this.supabase
        .from('authorized_article_categories' as 'article_categories')
        .select('*')
        .order('name', { ascending: true })
        .order('id', { ascending: true })
        .range(from, to),
    );
    return rows.map(mapCategory);
  }

  async create(input: CreateArticleCategoryInput): Promise<ArticleCategory> {
    const id = newArticleEntityId();
    await mutateArticleCatalogLookup(this.supabase, 'category', 'create', id, {
      name: input.name,
      parent_id: input.parentId ?? null,
      image_url: input.imageUrl ?? null,
    });
    const { data, error: readError } = await this.supabase
      .from('authorized_article_categories' as 'article_categories')
      .select('*')
      .eq('id', id)
      .single();
    if (readError) throw readError;
    return mapCategory(data as Record<string, unknown>);
  }

  async update(
    id: string,
    input: Partial<CreateArticleCategoryInput>,
  ): Promise<ArticleCategory> {
    const mapped: Record<string, unknown> = {};
    if (input.name !== undefined) mapped['name'] = input.name;
    if (input.parentId !== undefined) mapped['parent_id'] = input.parentId;
    if (input.imageUrl !== undefined) mapped['image_url'] = input.imageUrl;

    await mutateArticleCatalogLookup(
      this.supabase,
      'category',
      'update',
      id,
      mapped,
    );
    const { data, error: readError } = await this.supabase
      .from('authorized_article_categories' as 'article_categories')
      .select('*')
      .eq('id', id)
      .single();
    if (readError) throw readError;
    return mapCategory(data as Record<string, unknown>);
  }

  async delete(id: string): Promise<void> {
    await mutateArticleCatalogLookup(
      this.supabase,
      'category',
      'delete',
      id,
      {},
    );
  }
}

// `.in()` voyage dans la query string : au-delà de quelques centaines d'uuid
// l'URL dépasse le plafond ~32 ko de la passerelle, donc tout filtre par lot
// d'ids se découpe (même constante que sales/purchase-repository).
const IN_FILTER_CHUNK_SIZE = 500;

async function fetchAllArticlesAfterOffset(
  repository: Pick<IArticleRepository, 'list'>,
  filters: ArticleFilters | undefined,
  initialOffset: number,
): Promise<Article[]> {
  const rows: Article[] = [];
  for (let offset = initialOffset; ; offset += POSTGREST_PAGE_SIZE) {
    const page = await repository.list({
      ...filters,
      offset,
      limit: POSTGREST_PAGE_SIZE,
    });
    rows.push(...page);
    if (page.length < POSTGREST_PAGE_SIZE) return rows;
  }
}

/** Full Article shape minus the two supplier-cost fields. mapArticle turns the
 * omitted values into zero, so the repository contract stays stable while the
 * sensitive values never enter the response payload or React Query cache. */
const ARTICLE_LIST_COLUMNS_WITHOUT_COSTS = [
  'id',
  'code',
  'barcode',
  'name',
  'description',
  'category_id',
  'unit',
  'sale_price_ht',
  'vat_rate',
  'stock_min',
  'image_url',
  'is_active',
  'created_at',
  'updated_at',
  'family',
  'sub_family',
  'brand',
  'is_invoiceable',
  'sale_price_ttc',
  'initial_stock',
  'stock_tracking_enabled',
  'images',
  'packaging_units',
  'tenant_id',
].join(',');

/** Champ de recherche (domaine) → colonne Postgres. Table explicite : rien
 *  de ce que l'appelant fournit n'atterrit tel quel dans la requête. */
const ARTICLE_SEARCH_COLUMNS: Record<ArticleSearchField, string> = {
  name: 'name',
  code: 'code',
  barcode: 'barcode',
  description: 'description',
};

/** Colonnes à balayer : celles demandées, sinon toutes (comportement par
 *  défaut historique). Un champ inconnu est ignoré. */
function articleSearchColumns(fields?: ArticleSearchField[]): string[] {
  const requested = fields?.length
    ? fields.filter((f) => f in ARTICLE_SEARCH_COLUMNS)
    : [];
  const effective: readonly ArticleSearchField[] =
    requested.length > 0 ? requested : ARTICLE_SEARCH_FIELDS;
  return effective.map((field) => ARTICLE_SEARCH_COLUMNS[field]);
}

export class SupabaseArticleRepository implements IArticleRepository {
  private readonly generatedIdentifiersByRequest = new Map<
    string,
    { code?: string; barcode?: string }
  >();

  constructor(private supabase: TypedSupabaseClient) {}

  private async mutateArticle(
    action: 'update' | 'delete',
    id: string,
    payload: Record<string, unknown>,
  ): Promise<void> {
    const { data, error } = (await this.supabase.rpc(
      'mutate_article_atomic' as never,
      {
        p_action: action,
        p_article_id: id,
        p_payload: payload,
      } as never,
    )) as { data: string | null; error: Error | null };
    if (error) throw error;
    if (data !== id) throw new Error('article_mutation_failed');
  }

  private rememberGeneratedIdentifier(
    requestCacheKey: string,
    identifier: { code?: string; barcode?: string },
  ): void {
    this.generatedIdentifiersByRequest.set(requestCacheKey, {
      ...this.generatedIdentifiersByRequest.get(requestCacheKey),
      ...identifier,
    });
    if (this.generatedIdentifiersByRequest.size <= 128) return;
    const oldest = this.generatedIdentifiersByRequest.keys().next().value;
    if (oldest) this.generatedIdentifiersByRequest.delete(oldest);
  }

  async peekNextCode(): Promise<string> {
    return peekEntityCode(this.supabase, 'article');
  }

  async peekNextBarcode(): Promise<string> {
    return peekEntityCode(this.supabase, 'barcode');
  }

  private usesVariantSearch(filters?: ArticleFilters): boolean {
    return !!filters?.searchVariants && (!!filters.search?.trim() || Object.values(filters.variantAttributes ?? {}).some((value) => value.trim()));
  }

  private catalogQuery(filters?: ArticleFilters, countOnly = false) {
    const columns = countOnly ? 'id' : filters?.includeCosts === false ? ARTICLE_LIST_COLUMNS_WITHOUT_COSTS : '*';
    const source = this.supabase.from('authorized_articles' as 'articles').select(columns, countOnly ? { count: 'exact', head: true } : undefined);
    if (!this.usesVariantSearch(filters)) return source;
    const attributes = Object.fromEntries(Object.entries(filters?.variantAttributes ?? {})
      .filter(([, value]) => value.trim()).map(([name, value]) => [name.trim().toLowerCase(), value.trim().toLowerCase()]));
    // The additive read RPC returns the same authorized article projection.
    // Keep the generated-schema bridge confined to this adapter.
    const rpc = this.supabase.rpc('search_articles_with_variants' as never, {
      p_search: filters?.search ?? null,
      p_search_fields: filters?.searchFields ?? null,
      p_attributes: attributes,
      p_active_variants_only: filters?.activeVariantsOnly ?? false,
    } as never, countOnly ? { count: 'exact', head: true } : undefined) as unknown as typeof source;
    // JSON arguments make supabase-js send count-only RPCs as POST with
    // return=minimal. Calling select() would override that preference.
    return countOnly ? rpc : rpc.select(columns) as unknown as typeof source;
  }

  async list(filters?: ArticleFilters): Promise<Article[]> {
    // Une liste d'ids VIDE ne veut pas dire « pas de filtre » : elle demande
    // zéro article. Sans ce court-circuit, PostgREST recevrait `in.()` et
    // l'appelant récupérerait tout le catalogue.
    if (filters?.ids && filters.ids.length === 0) return [];
    if (filters?.ids && filters.ids.length > IN_FILTER_CHUNK_SIZE) {
      const chunks: string[][] = [];
      for (let i = 0; i < filters.ids.length; i += IN_FILTER_CHUNK_SIZE) {
        chunks.push(filters.ids.slice(i, i + IN_FILTER_CHUNK_SIZE));
      }
      const pages = await Promise.all(
        chunks.map((ids) => this.list({ ...filters, ids })),
      );
      return pages.flat();
    }
    let query = this.catalogQuery(filters);
    if (filters?.ids) {
      query = query.in('id', filters.ids);
    }
    // Delta du miroir hors-ligne — voir `ArticleFilters.updatedSince`.
    if (filters?.updatedSince) {
      query = query.gt('updated_at', filters.updatedSince);
    }
    const search = this.usesVariantSearch(filters) ? undefined : filters?.search?.trim();
    if (search) {
      query = query.or(
        orContainsAnyColumn(
          articleSearchColumns(filters?.searchFields),
          search,
        ),
      );
    }
    if (filters?.categoryId) {
      query = query.eq('category_id', filters.categoryId);
    }
    query = applyLooseTextFilter(query, 'family', filters?.family);
    query = applyLooseTextFilter(query, 'sub_family', filters?.subFamily);
    query = applyLooseTextFilter(query, 'brand', filters?.brand);
    query = applyLooseTextFilter(query, 'unit', filters?.unit);
    if (filters?.isActive !== undefined) {
      query = query.eq('is_active', filters.isActive);
    }
    if (filters?.isInvoiceable !== undefined) {
      query = query.eq('is_invoiceable', filters.isInvoiceable);
    }
    if (filters?.stockTrackingEnabled !== undefined) {
      query = query.eq('stock_tracking_enabled', filters.stockTrackingEnabled);
    }
    query = applyArticleSort(query, filters?.sort);
    if (filters?.limit && filters.limit > 0) {
      const offset = Math.max(0, filters.offset ?? 0);
      query = query.range(offset, offset + filters.limit - 1);
    }
    const { data, error } = await query;
    if (error) throw error;
    const firstPage = (
      (data ?? []) as unknown as Record<string, unknown>[]
    ).map((row) => mapArticle(row, this.supabase));
    if ((filters?.limit ?? 0) > 0 || firstPage.length < POSTGREST_PAGE_SIZE) {
      return firstPage;
    }

    // An omitted limit means “all”. PostgREST otherwise returns exactly its
    // first 1000 rows without signalling truncation, which previously made
    // document pickers and mobile catalogue consumers lose the tail.
    const tail = await fetchAllArticlesAfterOffset(
      this,
      filters,
      POSTGREST_PAGE_SIZE,
    );
    return firstPage.concat(tail);
  }

  /**
   * Compteurs et projections étroites du catalogue — voir `CatalogStats`.
   *
   * Une RPC parce que le tableau de bord n'a que faire des lignes : il lui faut
   * des nombres, et les obtenir en rapatriant 6 605 articles (4,2 Mo) était le
   * gros de l'egress. La fonction est `SECURITY INVOKER`, donc scopée par les
   * mêmes policies RLS que les tables — rien à filtrer ici.
   */
  async catalogStats(): Promise<CatalogStats> {
    const { data, error } = await this.supabase.rpc('catalog_stats');
    if (error) throw error;
    const stats = (data ?? {}) as Partial<
      Omit<CatalogStats, 'stockCostBuckets'>
    > & {
      stockCostBuckets?: [number, number, number][];
    };
    // La RPC renvoie déjà `[]` plutôt que null pour chaque liste ; ces replis
    // couvrent un déploiement où la fonction serait absente ou plus ancienne.
    return {
      total: Number(stats.total) || 0,
      active: Number(stats.active) || 0,
      byCategory: stats.byCategory ?? [],
      byFamily: stats.byFamily ?? [],
      stockMinArticles: stats.stockMinArticles ?? [],
      // Triplets positionnels côté SQL (voir la migration) : les noms de clés
      // répétés y pesaient 4× les valeurs. Seul endroit qui les décode.
      stockCostBuckets: (stats.stockCostBuckets ?? []).map(
        ([purchasePriceHt, supplierDiscount, quantity]) => ({
          purchasePriceHt: Number(purchasePriceHt) || 0,
          supplierDiscount: Number(supplierDiscount) || 0,
          quantity: Number(quantity) || 0,
        }),
      ),
    };
  }

  /**
   * L'article dont le code-barre OU le code vaut EXACTEMENT `scanned`.
   *
   * Sert la douchette. Auparavant l'écran chargeait le catalogue (`limit: 1000`,
   * ~647 ko) pour en construire une Map en mémoire : au-delà du millième article
   * le scan ne trouvait plus rien, silencieusement, et le catalogue repartait sur
   * le fil à chaque montage de page. Une égalité indexée coûte une ligne.
   *
   * `barcode` d'abord — c'est ce que porte l'étiquette ; `code` n'est qu'un repli
   * pour les catalogues où la douchette lit une référence interne. Si les deux
   * matchent sur des articles différents, celui du code-barre gagne.
   */
  async findByScanCode(scanned: string): Promise<Article | null> {
    return this.findByScanCodeFrom('authorized_articles', scanned);
  }

  async findOperationalArticleByScanCode(
    scanned: string,
  ): Promise<Article | null> {
    return this.findByScanCodeFrom('authorized_article_scan_catalog', scanned);
  }

  private async findByScanCodeFrom(
    source: 'authorized_articles' | 'authorized_article_scan_catalog',
    scanned: string,
  ): Promise<Article | null> {
    const value = scanned.trim();
    if (!value) return null;
    // Chaque colonne est interrogée séparément. Une requête OR limitée à deux
    // lignes peut rendre un code + un barcode et cacher un second barcode
    // dupliqué derrière la limite ; le scanner choisirait alors arbitrairement.
    // La séparation conserve la priorité barcode → code tout en faisant échouer
    // toute identité ambiguë de façon déterministe.
    for (const caseInsensitive of [false, true]) {
      for (const column of ['barcode', 'code'] as const) {
        const candidate = await this.scanColumnCandidates(
          source,
          column,
          value,
          caseInsensitive,
        );
        if (candidate) return candidate;
      }
    }
    return null;
  }

  /** Une colonne par passe : deux lignes suffisent alors à prouver l'ambiguïté. */
  private async scanColumnCandidates(
    source: 'authorized_articles' | 'authorized_article_scan_catalog',
    column: 'barcode' | 'code',
    value: string,
    caseInsensitive: boolean,
  ): Promise<Article | null> {
    let query = this.supabase.from(source as 'articles').select('*');
    query = caseInsensitive
      ? query.or(orEqualsCaseInsensitiveAnyColumn([column], value))
      : query.eq(column, value);
    const { data, error } = await query.limit(2);
    if (error) throw error;
    const rows = (data ?? []) as Record<string, unknown>[];
    if (rows.length === 0) return null;
    if (rows.length > 1) throw new Error('article_scan_ambiguous');
    return mapArticle(rows[0]!, this.supabase);
  }

  async count(filters?: ArticleFilters): Promise<number> {
    if (filters?.ids && filters.ids.length === 0) return 0;
    if (filters?.ids && filters.ids.length > IN_FILTER_CHUNK_SIZE) {
      let total = 0;
      for (let i = 0; i < filters.ids.length; i += IN_FILTER_CHUNK_SIZE) {
        // Les ids sont uniques : la somme des lots EST le total.
        total += await this.count({
          ...filters,
          ids: filters.ids.slice(i, i + IN_FILTER_CHUNK_SIZE),
        });
      }
      return total;
    }
    let query = this.catalogQuery(filters, true);
    if (filters?.ids) {
      query = query.in('id', filters.ids);
    }
    const search = this.usesVariantSearch(filters) ? undefined : filters?.search?.trim();
    if (search) {
      query = query.or(
        orContainsAnyColumn(
          articleSearchColumns(filters?.searchFields),
          search,
        ),
      );
    }
    if (filters?.categoryId) {
      query = query.eq('category_id', filters.categoryId);
    }
    query = applyLooseTextFilter(query, 'family', filters?.family);
    query = applyLooseTextFilter(query, 'sub_family', filters?.subFamily);
    query = applyLooseTextFilter(query, 'brand', filters?.brand);
    query = applyLooseTextFilter(query, 'unit', filters?.unit);
    if (filters?.isActive !== undefined) {
      query = query.eq('is_active', filters.isActive);
    }
    if (filters?.isInvoiceable !== undefined) {
      query = query.eq('is_invoiceable', filters.isInvoiceable);
    }
    if (filters?.stockTrackingEnabled !== undefined) {
      query = query.eq('stock_tracking_enabled', filters.stockTrackingEnabled);
    }
    const { count, error } = await query;
    if (error) throw error;
    return count ?? 0;
  }

  /**
   * Prix d'achat + seuil de tout le catalogue, rien d'autre.
   *
   * `list()` renvoie des lignes larges (description, tableau d'images, prix
   * boutique/pro) : en tirer 6 000 pour additionner une valorisation coûtait
   * ~3,5 Mo à chaque ouverture de la page Articles. Ces cinq colonnes suffisent
   * — le calcul, lui, vit dans `@erp/domain/inventory`.
   *
   * Paginé : le cap PostgREST à 1000 lignes tronquerait silencieusement le
   * catalogue, et un total de stock tronqué est pire qu'absent.
   */
  async listValuationRows(): Promise<ArticleValuationRow[]> {
    const rows = await fetchAllRows((from, to) =>
      this.supabase
        .from('authorized_articles' as 'articles')
        .select(
          'id, purchase_price_ht, supplier_discount, stock_min, is_active, stock_tracking_enabled',
        )
        .order('id', { ascending: true })
        .range(from, to),
    );
    return (rows as unknown as Record<string, unknown>[]).map((row) => ({
      id: row['id'] as string,
      stockTrackingEnabled: (row['stock_tracking_enabled'] as boolean) ?? false,
      purchasePriceHt: Number(row['purchase_price_ht']) || 0,
      supplierDiscount: Number(row['supplier_discount']) || 0,
      stockMin: Number(row['stock_min']) || 0,
      isActive: (row['is_active'] as boolean) ?? true,
    }));
  }

  async getById(id: string): Promise<Article | null> {
    const { data, error } = await this.supabase
      .from('authorized_articles' as 'articles')
      .select('*')
      .eq('id', id)
      .single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return mapArticle(data, this.supabase);
  }

  async create(input: CreateArticleInput): Promise<Article> {
    const tenantId = await resolveCurrentTenantId(this.supabase);
    const requestId = input.clientRequestId ?? newArticleEntityId();
    const requestCacheKey = `${tenantId}:${requestId}`;
    const generated = this.generatedIdentifiersByRequest.get(requestCacheKey);
    const code =
      input.code && input.code.trim() !== ''
        ? input.code.trim()
        : (generated?.code ??
          (await consumeEntityCode(this.supabase, 'article')));
    if (!input.code?.trim() && !generated?.code) {
      this.rememberGeneratedIdentifier(requestCacheKey, { code });
    }
    // Barcode : auto-génération explicite via flag, sinon on prend la valeur
    // saisie telle quelle (ou null si vide).
    let barcode: string | null;
    if (input.autoGenerateBarcode) {
      barcode =
        generated?.barcode ??
        (await consumeEntityCode(this.supabase, 'barcode'));
      if (!generated?.barcode) {
        this.rememberGeneratedIdentifier(requestCacheKey, { barcode });
      }
    } else {
      const rawBarcode = input.barcode?.trim();
      barcode = rawBarcode && rawBarcode !== '' ? rawBarcode : null;
    }
    // Always persist absolute public URLs. Importers (and any caller) may pass
    // a bare filename like `234aaaf9-….png`; expand it to its `erp-images`
    // public URL here so we never store a path that 404s when rendered raw.
    // Already-absolute values pass through untouched (idempotent).
    const articleRow = {
      tenant_id: tenantId,
      code,
      name: input.name,
      sale_price_ht: input.salePriceHt ?? 0,
      sale_price_ttc: input.salePriceTtc ?? 0,
      barcode,
      description: input.description,
      category_id: input.categoryId,
      family: input.family,
      sub_family: input.subFamily,
      brand: input.brand,
      unit: input.unit ?? 'pcs',
      purchase_price_ht: input.purchasePriceHt ?? 0,
      supplier_discount: input.supplierDiscount ?? 0,
      vat_rate: input.vatRate ?? 19,
      stock_min: input.stockMin ?? 0,
      stock_tracking_enabled: input.stockTrackingEnabled ?? false,
      initial_stock: input.initialStock ?? 0,
      images: absolutizeArticleImages(
        this.supabase,
        input.images ?? [],
        tenantId,
      ),
      image_url: resolveStorageImageUrl(this.supabase, input.imageUrl, {
        tenantId,
        folder: 'articles',
      }),
      is_active: input.isActive ?? true,
      is_invoiceable: input.isInvoiceable ?? true,
    };
    const initialStocks = normalizeInitialArticleStocks(
      input.stockTrackingEnabled ?? false,
      input.initialStocks ?? [],
    );
    const { data, error } = (await this.supabase.rpc(
      'create_article_with_initial_stock_atomic' as never,
      {
        p_article: articleRow,
        p_initial_stocks: initialStocks.map((stock) => ({
          warehouse_id: stock.warehouseId,
          quantity: stock.quantity,
        })),
        p_client_request_id: requestId,
      } as never,
    )) as {
      data: Record<string, unknown> | null;
      error: Error | null;
    };
    if (error) throw error;
    if (!data) throw new Error('article_not_created');
    const created = mapArticle(data, this.supabase);
    // Les conditionnements ne passent pas par la RPC atomique de création (qui
    // insère une liste de colonnes fixe) : on les pose juste après, via le
    // chemin `update` direct de la table `articles`. Le stock d'ouverture, lui,
    // reste bien atomique. Un article mono-unité ne paie aucun write en plus.
    const packagingUnits = sanitizePackagingUnits(input.packagingUnits);
    if (packagingUnits.length > 0) {
      return this.update(created.id, { packagingUnits });
    }
    return created;
  }

  async update(id: string, input: UpdateArticleInput): Promise<Article> {
    const mapped: TablesUpdate<'articles'> = {};
    if (input.code !== undefined) mapped['code'] = input.code;
    if (input.name !== undefined) mapped['name'] = input.name;
    if (input.salePriceHt !== undefined)
      mapped['sale_price_ht'] = input.salePriceHt;
    if (input.salePriceTtc !== undefined)
      mapped['sale_price_ttc'] = input.salePriceTtc;
    if (input.barcode !== undefined) mapped['barcode'] = input.barcode;
    if (input.description !== undefined)
      mapped['description'] = input.description;
    if (input.categoryId !== undefined)
      mapped['category_id'] = input.categoryId;
    if (input.family !== undefined) mapped['family'] = input.family;
    if (input.subFamily !== undefined) mapped['sub_family'] = input.subFamily;
    if (input.brand !== undefined) mapped['brand'] = input.brand;
    if (input.unit !== undefined) mapped['unit'] = input.unit;
    if (input.packagingUnits !== undefined) {
      // JSONB : on assainit au bord de la persistance pour que la base ne
      // stocke jamais un conditionnement invalide (facteur non entier, libellé
      // vide, doublon de facteur). PackagingUnit est une interface sans index
      // signature ; sa représentation assainie reste une valeur JSON.
      mapped.packaging_units = sanitizePackagingUnits(
        input.packagingUnits,
      ) as unknown as Json;
    }
    if (input.purchasePriceHt !== undefined)
      mapped['purchase_price_ht'] = input.purchasePriceHt;
    if (input.supplierDiscount !== undefined)
      mapped['supplier_discount'] = input.supplierDiscount;
    if (input.vatRate !== undefined) mapped['vat_rate'] = input.vatRate;
    if (input.stockMin !== undefined) mapped['stock_min'] = input.stockMin;
    if (input.stockTrackingEnabled !== undefined) {
      (
        mapped as TablesUpdate<'articles'> & {
          stock_tracking_enabled?: boolean;
        }
      ).stock_tracking_enabled = input.stockTrackingEnabled;
    }
    if (input.initialStock !== undefined)
      mapped['initial_stock'] = input.initialStock;
    // Same absolutization as create(): never persist a bare path. We only pay
    // for the tenant lookup when an incoming image value is actually bare —
    // the form upload path already sends absolute URLs (pass-through, no RPC).
    if (input.images !== undefined || input.imageUrl !== undefined) {
      const hasBare =
        (input.images ?? []).some(isBareStoragePath) ||
        isBareStoragePath(input.imageUrl);
      const tenantId = hasBare
        ? await resolveCurrentTenantId(this.supabase)
        : null;
      if (input.images !== undefined) {
        mapped['images'] = absolutizeArticleImages(
          this.supabase,
          input.images ?? [],
          tenantId,
        );
      }
      if (input.imageUrl !== undefined) {
        mapped['image_url'] = resolveStorageImageUrl(
          this.supabase,
          input.imageUrl,
          { tenantId, folder: 'articles' },
        );
      }
    }
    if (input.isActive !== undefined) mapped['is_active'] = input.isActive;
    if (input.isInvoiceable !== undefined)
      mapped['is_invoiceable'] = input.isInvoiceable;

    await this.mutateArticle('update', id, mapped);

    // Un prix (ou un coût) qui bouge doit se répercuter sur les documents
    // ProSpace encore vivants : devis réalignés, commission des commandes non
    // expédiées recalculée. Voir pro-price-propagation.ts.
    if (touchesProPricing(input)) {
      await runProPricePropagation(this.supabase, [id]);
    }

    const updated = await this.getById(id);
    if (!updated) throw new Error('article_read_after_update_denied');
    return updated;
  }

  async delete(id: string): Promise<void> {
    await this.mutateArticle('delete', id, {});
  }
}

// Organisation columns are loose VARCHARs that can store either a UUID (new
// rows) or a raw name (legacy/imported rows). When callers send an array we
// treat it as "match any of these candidate values" via `.in()`.
function applyLooseTextFilter<
  Q extends {
    eq: (col: string, value: string) => Q;
    in: (col: string, values: string[]) => Q;
  },
>(query: Q, column: string, value: string | string[] | undefined): Q {
  if (value === undefined) return query;
  if (Array.isArray(value)) {
    const cleaned = value.filter((v) => typeof v === 'string' && v.length > 0);
    if (cleaned.length === 0) return query;
    if (cleaned.length === 1) return query.eq(column, cleaned[0]!);
    return query.in(column, cleaned);
  }
  if (value === '') return query;
  return query.eq(column, value);
}

// Expand a list of (possibly bare) image paths to absolute `erp-images` public
// URLs, dropping any that resolve to empty. Mirrors the read-side resolution in
// mapArticle so writes and reads agree on what an article's `images[]` holds.
function absolutizeArticleImages(
  supabase: TypedSupabaseClient,
  images: string[],
  tenantId: string | null,
): string[] {
  return images
    .map((value) =>
      resolveStorageImageUrl(supabase, value, { tenantId, folder: 'articles' }),
    )
    .filter((url): url is string => typeof url === 'string' && url.length > 0);
}

// UI column key → articles DB column, for server-side ordering. Computed or
// resolved-by-name columns (category/family/brand, ttc prices, margin, stock,
// publication flags) are absent: they can't be ordered server-side.
const ARTICLE_SORT_COLUMNS: Record<string, string> = {
  code: 'code',
  barcode: 'barcode',
  name: 'name',
  description: 'description',
  unit: 'unit',
  purchasePriceHt: 'purchase_price_ht',
  supplierDiscount: 'supplier_discount',
  salePriceHt: 'sale_price_ht',
  salePriceTtc: 'sale_price_ttc',
  vatRate: 'vat_rate',
  stockMin: 'stock_min',
  initialStock: 'initial_stock',
  isActive: 'is_active',
  isInvoiceable: 'is_invoiceable',
  createdAt: 'created_at',
  updatedAt: 'updated_at',
  // Hour-only display columns — they sort by the timestamp they render.
  createdTime: 'created_at',
  updatedTime: 'updated_at',
};

function applyArticleSort(
  query: any,
  sort?: { field: string; direction: 'asc' | 'desc' },
): any {
  const column = sort ? ARTICLE_SORT_COLUMNS[sort.field] : undefined;
  if (column) {
    query = query.order(column, { ascending: sort!.direction === 'asc' });
    // Deterministic tiebreaker (also the default order) so equal values page stably.
    if (column !== 'name') query = query.order('name', { ascending: true });
    // `name` is not unique (248 duplicate names on the biggest catalog) — see
    // withStableOrder.
    return withStableOrder(query);
  }
  return withStableOrder(query.order('name'));
}

function mapArticle(
  row: Record<string, unknown>,
  supabase?: TypedSupabaseClient,
): Article {
  // Resolve legacy bare image paths (e.g. `234aaaf9-….png`) to full public
  // URLs. Without this, imported/legacy rows whose `image_url` / `images[]`
  // store a bare filename render as a broken `<img>` everywhere they're shown
  // (articles pages, Pro products, erp-pro electrician catalogue). Already
  // absolute URLs pass through; empty values become null → placeholder.
  const tenantId = (row['tenant_id'] as string | null) ?? null;
  const resolveImg = (value: string | null | undefined): string | null =>
    supabase
      ? resolveStorageImageUrl(supabase, value, {
          tenantId,
          folder: 'articles',
        })
      : typeof value === 'string' && value.trim()
        ? value.trim()
        : null;
  const images = ((row['images'] as string[] | null) ?? [])
    .map(resolveImg)
    .filter((url): url is string => typeof url === 'string' && url.length > 0);
  return {
    id: row['id'] as string,
    code: row['code'] as string,
    barcode: (row['barcode'] as string) ?? null,
    name: row['name'] as string,
    description: (row['description'] as string) ?? null,
    categoryId: (row['category_id'] as string) ?? null,
    family: (row['family'] as string) ?? null,
    subFamily: (row['sub_family'] as string) ?? null,
    brand: (row['brand'] as string) ?? null,
    unit: (row['unit'] as string) ?? 'pcs',
    packagingUnits: sanitizePackagingUnits(row['packaging_units']),
    purchasePriceHt: (row['purchase_price_ht'] as number) ?? 0,
    supplierDiscount: (row['supplier_discount'] as number) ?? 0,
    salePriceHt: (row['sale_price_ht'] as number) ?? 0,
    salePriceTtc: (row['sale_price_ttc'] as number) ?? 0,
    vatRate: (row['vat_rate'] as number) ?? 19,
    stockMin: (row['stock_min'] as number) ?? 0,
    stockTrackingEnabled: (row['stock_tracking_enabled'] as boolean) ?? false,
    initialStock: (row['initial_stock'] as number) ?? 0,
    images,
    imageUrl: resolveImg(row['image_url'] as string | null),
    isActive: (row['is_active'] as boolean) ?? true,
    isInvoiceable: (row['is_invoiceable'] as boolean) ?? true,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

function mapCategory(r: Record<string, unknown>): ArticleCategory {
  return {
    id: r['id'] as string,
    name: r['name'] as string,
    parentId: (r['parent_id'] as string) ?? null,
    imageUrl: (r['image_url'] as string) ?? null,
  };
}

// ── Families ──────────────────────────────────────────────────────

export class SupabaseArticleFamilyRepository implements IArticleFamilyRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async list(): Promise<ArticleFamily[]> {
    const rows = await fetchAllRows<Record<string, unknown>>((from, to) =>
      this.supabase
        .from('authorized_article_families' as 'article_families')
        .select('*')
        .order('name', { ascending: true })
        .order('id', { ascending: true })
        .range(from, to),
    );
    return rows.map((r) => ({
      id: r['id'] as string,
      name: r['name'] as string,
    }));
  }

  async create(name: string): Promise<ArticleFamily> {
    const id = newArticleEntityId();
    await mutateArticleCatalogLookup(this.supabase, 'family', 'create', id, {
      name,
    });
    const { data, error: readError } = await this.supabase
      .from('authorized_article_families' as 'article_families')
      .select('*')
      .eq('id', id)
      .single();
    if (readError) throw readError;
    const row = data as Record<string, unknown>;
    return { id: row['id'] as string, name: row['name'] as string };
  }

  async update(id: string, input: { name: string }): Promise<ArticleFamily> {
    await mutateArticleCatalogLookup(this.supabase, 'family', 'update', id, {
      name: input.name,
    });
    const { data, error: readError } = await this.supabase
      .from('authorized_article_families' as 'article_families')
      .select('*')
      .eq('id', id)
      .single();
    if (readError) throw readError;
    const row = data as Record<string, unknown>;
    return { id: row['id'] as string, name: row['name'] as string };
  }

  async delete(id: string): Promise<void> {
    await mutateArticleCatalogLookup(this.supabase, 'family', 'delete', id, {});
  }
}

// ── Brands ────────────────────────────────────────────────────────

export class SupabaseArticleBrandRepository implements IArticleBrandRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async list(): Promise<ArticleBrand[]> {
    const rows = await fetchAllRows<Record<string, unknown>>((from, to) =>
      this.supabase
        .from('authorized_article_brands' as 'article_brands')
        .select('*')
        .order('name', { ascending: true })
        .order('id', { ascending: true })
        .range(from, to),
    );
    return rows.map(mapBrand);
  }

  async create(input: CreateArticleBrandInput): Promise<ArticleBrand> {
    const id = newArticleEntityId();
    await mutateArticleCatalogLookup(this.supabase, 'brand', 'create', id, {
      name: input.name,
      image_url: input.imageUrl ?? null,
    });
    const { data, error: readError } = await this.supabase
      .from('authorized_article_brands' as 'article_brands')
      .select('*')
      .eq('id', id)
      .single();
    if (readError) throw readError;
    return mapBrand(data as Record<string, unknown>);
  }

  async update(
    id: string,
    input: Partial<CreateArticleBrandInput>,
  ): Promise<ArticleBrand> {
    const mapped: Record<string, unknown> = {};
    if (input.name !== undefined) mapped['name'] = input.name;
    if (input.imageUrl !== undefined) mapped['image_url'] = input.imageUrl;
    await mutateArticleCatalogLookup(
      this.supabase,
      'brand',
      'update',
      id,
      mapped,
    );
    const { data, error: readError } = await this.supabase
      .from('authorized_article_brands' as 'article_brands')
      .select('*')
      .eq('id', id)
      .single();
    if (readError) throw readError;
    return mapBrand(data as Record<string, unknown>);
  }

  async delete(id: string): Promise<void> {
    await mutateArticleCatalogLookup(this.supabase, 'brand', 'delete', id, {});
  }
}

async function mutateArticleCatalogLookup(
  supabase: TypedSupabaseClient,
  entity: 'category' | 'family' | 'brand',
  action: 'create' | 'update' | 'delete',
  id: string,
  payload: Record<string, unknown>,
): Promise<void> {
  const { data, error } = (await supabase.rpc(
    'mutate_article_catalog_lookup' as never,
    {
      p_entity: entity,
      p_action: action,
      p_id: id,
      p_payload: payload,
    } as never,
  )) as { data: string | null; error: Error | null };
  if (error) throw error;
  if (data !== id) throw new Error('article_catalog_lookup_mutation_failed');
}

async function resolveCurrentTenantId(
  supabase: TypedSupabaseClient,
): Promise<string> {
  const { data, error } = await (
    supabase.rpc as unknown as (
      fn: string,
      args: Record<string, unknown>,
    ) => Promise<{ data: string | null; error: { message: string } | null }>
  ).call(supabase, 'current_user_tenant_id', {});

  if (error) throw error;
  if (!data) throw new Error('current_tenant_not_found');
  return data;
}

function mapBrand(r: Record<string, unknown>): ArticleBrand {
  return {
    id: r['id'] as string,
    name: r['name'] as string,
    imageUrl: (r['image_url'] as string) ?? null,
  };
}
